import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, RotateCcw, ShieldCheck, Upload } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { Section, Empty, useBusy } from "../common";
import { fmtDateTime } from "../../lib/helpers";
import { RestoreDialog } from "./RestoreDialog";

const total = (c) => Object.values(c || {}).reduce((a, b) => a + b, 0);

export function BackupsPanel() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [restore, setRestore] = useState(null);
  const [busy, run] = useBusy();
  const fileRef = useRef();
  const importFile = (file) => file && run(async () => {
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/platform/backups/import", fd); toast.success(t("Backup file imported", "Archivo de copia importado")); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { fileRef.current.value = ""; }
  });
  const load = () => api.get("/platform/backups").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const now = () => run(async () => {
    try { await api.post("/platform/backups"); toast.success(t("Backup created and verified", "Copia creada y verificada")); load(); } catch (e) { toast.error(errMsg(e)); }
  });
  const download = async (b) => {
    try {
      const r = await api.get(`/platform/backups/${b.id}/download`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `backup-${b.created_at.slice(0, 19).replace(/:/g, "-")}.json.gz`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title={t("Backups (daily + before every migration)", "Copias de seguridad (diarias + antes de cada migración)")} testid="backups-panel"
      action={<div className="flex flex-wrap justify-end gap-2 [&>button]:whitespace-nowrap">
        <input ref={fileRef} type="file" hidden accept=".gz,application/gzip" onChange={(e) => importFile(e.target.files[0])} data-testid="backup-import-input" />
        <button className="btn-outline btn-sm" disabled={busy} onClick={() => fileRef.current.click()} data-testid="backup-import-btn"><Upload className="w-4 h-4" /> {t("Import", "Importar")}</button>
        <button className="btn-dark btn-sm" disabled={busy} onClick={now} data-testid="backup-now-btn"><DatabaseBackup className="w-4 h-4" /> {t("Back up", "Respaldar")}</button>
      </div>}>
      {!items.length && <Empty text={t("No backups yet", "Sin copias aún")} testid="backups-empty" />}
      {items.map((b) => (
        <div key={b.id} className="px-4 py-3 border-b flex flex-wrap items-center gap-3" data-testid={`backup-row-${b.id}`}>
          <div className="flex-1 min-w-[200px]">
            <div className="font-semibold text-sm">{fmtDateTime(b.created_at, lang)} {b.verified && <ShieldCheck className="w-4 h-4 inline text-emerald-600" aria-label="verified" />}</div>
            <div className="text-xs text-slate-500">{b.reason} · {b.companies} {t("companies", "empresas")} · {total(b.counts)} {t("records", "registros")} · {(b.size / 1024).toFixed(0)} KB</div>
          </div>
          <button className="btn-outline btn-sm" onClick={() => download(b)} data-testid={`backup-download-${b.id}`}><Download className="w-4 h-4" /> {t("Download", "Descargar")}</button>
          <button className="btn-outline btn-sm" onClick={() => setRestore(b)} data-testid={`backup-restore-${b.id}`}><RotateCcw className="w-4 h-4" /> {t("Restore", "Restaurar")}</button>
        </div>
      ))}
      {restore && <RestoreDialog backup={restore} onClose={() => { setRestore(null); load(); }} />}
    </Section>
  );
}
